/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import StatusBoxRow from '../statusBoxRow';
import {
  useFundPensionStatus,
  useMandateDeadlines,
  usePendingApplications,
} from '../../../common/apiHooks';
import {
  Application,
  Conversion,
  Fund,
  MandateDeadlines,
  SourceFund,
} from '../../../common/apiModels';
import { State } from '../../../../types';
import { InfoTooltip } from '../../../common/infoTooltip/InfoTooltip';
import { isTuleva } from '../../../common/utils';
import { getValueSum } from '../../AccountStatement/fundSelector';
import { Euro } from '../../../common/Euro';
import { Shimmer } from '../../../common/shimmer/Shimmer';
import { formatDate, formatDateFrom, formatDateUntil } from '../../../common/dateFormatter';
import { isDateSameOrBeforeCancellationDeadline } from '../../ApplicationSection/ApplicationFunctions';
import {
  SecondPillarPaymentRateTaxWin,
  useSecondPillarTaxWin,
} from '../../../flows/secondPillarPaymentRate/SecondPillarPaymentRateTaxWin';
import { ActiveFundPensionDescription } from '../ActiveFundPensionDescription';
import { FundPension } from '../../../common/apiModels/withdrawals';
import { PaymentRateSubRow } from './PaymentRateSubRow';
import { NudgeKey, PaymentRateSeason } from '../../../common/apiModels/nudge';
import { TranslationKey } from '../../../translations';
import styles from './SecondPillarStatusBox.module.scss';

export interface Props {
  loading: boolean;
  conversion: Conversion;
  sourceFunds: SourceFund[];
  targetFunds: Fund[];
  secondPillarActive: boolean;
  currentPaymentRate: number;
  pendingPaymentRate: number;
  activeFundIsin: string | undefined;
  paymentRateSeason?: PaymentRateSeason;
  nudgeKey?: NudgeKey;
}

type RowProps = {
  loading: boolean;
  conversion: Conversion;
  currentPaymentRate: number;
  pendingPaymentRate: number;
  sourceFunds: SourceFund[];
  targetFunds: Fund[];
  mandateDeadlines: MandateDeadlines | undefined;
  leaveApplication?: Application | undefined;
  paymentRateSeason?: PaymentRateSeason;
  nudgeKey?: NudgeKey;
};

export const SecondPillarStatusBox: React.FC<Props> = ({
  loading,
  conversion,
  sourceFunds,
  targetFunds,
  secondPillarActive,
  currentPaymentRate,
  pendingPaymentRate,
  activeFundIsin,
  paymentRateSeason,
  nudgeKey,
}: Props) => {
  // TODO improve loading state handling here
  const { data: mandateDeadlines } = useMandateDeadlines();
  const { data: applications } = usePendingApplications();
  const { data: fundPensionStatus } = useFundPensionStatus();

  const leaveApplication: Application | undefined =
    applications &&
    applications.find(
      (application) => application.type === 'TRANSFER' && isTuleva(application.details.sourceFund),
    );

  const rowProps: RowProps = {
    loading,
    conversion,
    sourceFunds,
    currentPaymentRate,
    pendingPaymentRate,
    targetFunds,
    mandateDeadlines,
    leaveApplication,
    paymentRateSeason,
    nudgeKey,
  };

  if (!secondPillarActive) {
    return <SecondPillarMissing {...rowProps} />;
  }

  if (conversion.pendingWithdrawal) {
    return <PendingPartialWithdrawalOrFundPension {...rowProps} />;
  }

  const activeSecondPillarFundPension = fundPensionStatus?.fundPensions.find(
    (fundPension) => fundPension.active && fundPension.pillar === 'SECOND',
  );

  if (activeSecondPillarFundPension) {
    return <ActiveFundPension fundPension={activeSecondPillarFundPension} />;
  }

  const isFullyConvertedToTuleva = conversion.selectionComplete && conversion.transfersComplete;

  if (!isFullyConvertedToTuleva && conversion.weightedAverageFee >= 0.003) {
    return <HighFee {...rowProps} />;
  }

  if (pendingPaymentRate < 6) {
    return paymentRateSeason ? (
      <SeasonPaymentRateRow {...rowProps} paymentRateSeason={paymentRateSeason} />
    ) : (
      <IncreasePaymentRate {...rowProps} />
    );
  }

  if (!isFullyConvertedToTuleva) {
    return <InLowFeeFund {...rowProps} />;
  }

  const TULEVA_2ND_PILLAR_BOND_FUND_ISIN = 'EE3600109443';
  if (activeFundIsin === TULEVA_2ND_PILLAR_BOND_FUND_ISIN) {
    return <FullyConvertedToTulevaBonds {...rowProps} />;
  }

  return paymentRateSeason ? (
    <SeasonPaymentRateRow {...rowProps} paymentRateSeason={paymentRateSeason} />
  ) : (
    <FullyConvertedToTuleva {...rowProps} />
  );
};

type SeasonRowState = 'raise' | 'maximum' | 'pendingRaise' | 'pendingPartial' | 'pendingDecrease';

type SeasonRow = {
  status: 'SUCCESS' | 'WARNING';
  line1: TranslationKey;
  line2: TranslationKey;
  line2WithoutTaxWin: TranslationKey;
  action?: TranslationKey;
  showsPill?: boolean;
};

const SEASON_ROWS: Record<SeasonRowState, SeasonRow> = {
  raise: {
    status: 'WARNING',
    line1: 'account.status.choice.pillar.second.season.raise.line1',
    line2: 'account.status.choice.pillar.second.season.raise.line2',
    line2WithoutTaxWin: 'account.status.choice.pillar.second.season.raise.line2.noTaxWin',
    action: 'account.status.choice.paymentRate.increase',
    showsPill: true,
  },
  maximum: {
    status: 'SUCCESS',
    line1: 'account.status.choice.pillar.second.season.maximum.line1',
    line2: 'account.status.choice.pillar.second.season.maximum.line2',
    line2WithoutTaxWin: 'account.status.choice.pillar.second.season.maximum.line2.noTaxWin',
  },
  pendingRaise: {
    status: 'SUCCESS',
    line1: 'account.status.choice.pillar.second.season.pending.line1',
    line2: 'account.status.choice.pillar.second.season.pendingRaise.line2',
    line2WithoutTaxWin: 'account.status.choice.pillar.second.season.pendingRaise.line2.noTaxWin',
  },
  pendingPartial: {
    status: 'WARNING',
    line1: 'account.status.choice.pillar.second.season.pending.line1',
    line2: 'account.status.choice.pillar.second.season.pendingPartial.line2',
    line2WithoutTaxWin: 'account.status.choice.pillar.second.season.pendingPartial.line2.noTaxWin',
    action: 'account.status.choice.paymentRate.increase',
  },
  pendingDecrease: {
    status: 'WARNING',
    line1: 'account.status.choice.pillar.second.season.pendingDecrease.line1',
    line2: 'account.status.choice.pillar.second.season.pendingDecrease.line2',
    line2WithoutTaxWin: 'account.status.choice.pillar.second.season.pendingDecrease.line2.noTaxWin',
    action: 'account.status.choice.paymentRate.change',
  },
};

const seasonRowState = (currentPaymentRate: number, pendingPaymentRate: number): SeasonRowState => {
  if (pendingPaymentRate === currentPaymentRate) {
    return currentPaymentRate === 6 ? 'maximum' : 'raise';
  }
  if (pendingPaymentRate < currentPaymentRate) {
    return 'pendingDecrease';
  }
  return pendingPaymentRate === 6 ? 'pendingRaise' : 'pendingPartial';
};

const SeasonPaymentRateRow = ({
  loading,
  currentPaymentRate,
  pendingPaymentRate,
  paymentRateSeason,
}: RowProps & { paymentRateSeason: PaymentRateSeason }) => {
  const { taxWin, loading: loadingTaxWin } = useSecondPillarTaxWin();
  const row = SEASON_ROWS[seasonRowState(currentPaymentRate, pendingPaymentRate)];
  const emphasizeDeadline = paymentRateSeason.mode === 'LAST_DAYS';
  const values = {
    currentPaymentRate,
    pendingPaymentRate,
    deadline: formatDate(paymentRateSeason.deadline),
    deadlineUntil: formatDateUntil(paymentRateSeason.deadline),
    fulfillmentDate: formatDateFrom(paymentRateSeason.fulfillmentDate),
    ...(taxWin === null ? {} : { taxWin: `${taxWin.toFixed(0)}\u00A0€` }),
    b: (chunks: string) => (emphasizeDeadline ? <b>{chunks}</b> : <>{chunks}</>),
    hl: (chunks: string) => <span className="text-primary fw-semibold">{chunks}</span>,
    link: (chunks: string) => <Link to="/2nd-pillar-tax-win">{chunks}</Link>,
    amount: (chunks: string) => <b>{chunks}</b>,
  };

  return (
    <StatusBoxRow
      status={row.status}
      showAction={!loading}
      name={
        <span className="d-inline-flex flex-wrap align-items-center gap-2">
          <FormattedMessage id="account.status.choice.pillar.second" />
          {row.showsPill && (
            <span className={`${styles.pill} text-nowrap`}>
              <FormattedMessage
                id="account.status.choice.pillar.second.season.pill"
                values={values}
              />
            </span>
          )}
        </span>
      }
      lines={[
        <FormattedMessage id={row.line1} values={values} />,
        loadingTaxWin ? (
          <Shimmer height={24} />
        ) : (
          <span className="text-body-secondary">
            <FormattedMessage
              id={taxWin === null ? row.line2WithoutTaxWin : row.line2}
              values={values}
            />
          </span>
        ),
      ]}
    >
      {row.action && (
        <Link to="/2nd-pillar-payment-rate" className="btn btn-primary">
          <FormattedMessage id={row.action} />
        </Link>
      )}
    </StatusBoxRow>
  );
};

const FeeComparison = ({
  currentFeesEuro,
  tulevaFeesEuro,
}: {
  currentFeesEuro: number;
  tulevaFeesEuro: number;
}) => (
  <span className="text-body-secondary">
    <FormattedMessage
      id="account.status.choice.highFee.comment"
      values={{
        currentFeesEuro: (
          <Euro amount={currentFeesEuro} className="text-danger" fractionDigits={0} />
        ),
        tulevaFeesEuro: (
          <Euro amount={tulevaFeesEuro} className="text-success" fractionDigits={0} />
        ),
      }}
    />
  </span>
);

function SecondPillarActionButton({
  leaveApplication,
  className,
}: {
  leaveApplication: Application | undefined;
  className: string;
}) {
  return (
    <>
      {leaveApplication && isDateSameOrBeforeCancellationDeadline(leaveApplication) ? (
        <Link
          to={`/applications/${leaveApplication.id}/cancellation`}
          className={`btn ${className}`}
        >
          <FormattedMessage id="account.status.choice.cancel.application" />
        </Link>
      ) : (
        <Link to="/2nd-pillar-flow" className={`btn ${className}`}>
          <FormattedMessage id="account.status.choice.bring.to.tuleva" />
        </Link>
      )}
    </>
  );
}

const PendingPartialWithdrawalOrFundPension = ({ loading, mandateDeadlines }: RowProps) => {
  const pendingEarlyWithdrawal = usePendingEarlyWithdrawalApplication();
  const pendingPartialWithdrawal = usePendingPartialWithdrawalApplication();
  const pendingFundPensionOpening = usePendingFundPensionOpeningApplication();

  if (pendingEarlyWithdrawal) {
    return (
      <StatusBoxRow
        status="ERROR"
        showAction={!loading}
        name={<FormattedMessage id="account.status.choice.pillar.second" />}
        lines={[<FormattedMessage id="account.status.choice.pillar.second.withdraw" />]}
      >
        <Link
          to={`/applications/${pendingEarlyWithdrawal.id}/cancellation`}
          className="btn btn-primary"
        >
          <FormattedMessage id="account.status.choice.pillar.second.withdraw.cancel" />
        </Link>
      </StatusBoxRow>
    );
  }

  if (pendingFundPensionOpening && pendingPartialWithdrawal) {
    return (
      <StatusBoxRow
        status="WARNING"
        showAction={false}
        name={<FormattedMessage id="account.status.choice.pillar.second" />}
        lines={[
          <FormattedMessage id="account.status.choice.pillar.second.fundPensionOpeningPartialWithdrawal" />,
          <span className="text-body-secondary">
            <FormattedMessage
              id="account.status.choice.pillar.second.withdrawalContributionEndingDisclaimer.plural"
              values={{
                b: (chunks: string) => <b>{chunks}</b>,
                contributionEndDate: formatDate(mandateDeadlines?.secondPillarContributionEndDate),
              }}
            />
          </span>,
        ]}
      />
    );
  }

  if (pendingPartialWithdrawal) {
    return (
      <StatusBoxRow
        status="WARNING"
        showAction={false}
        name={<FormattedMessage id="account.status.choice.pillar.second" />}
        lines={[
          <FormattedMessage id="account.status.choice.pillar.second.partialWithdrawal" />,
          <span className="text-body-secondary">
            <FormattedMessage
              id="account.status.choice.pillar.second.withdrawalContributionEndingDisclaimer.singular"
              values={{
                b: (chunks: string) => <b>{chunks}</b>,
                contributionEndDate: formatDate(mandateDeadlines?.secondPillarContributionEndDate),
              }}
            />
          </span>,
        ]}
      />
    );
  }

  if (pendingFundPensionOpening) {
    return (
      <StatusBoxRow
        status="WARNING"
        showAction={false}
        name={<FormattedMessage id="account.status.choice.pillar.second" />}
        lines={[
          <FormattedMessage id="account.status.choice.pillar.second.fundPensionOpening" />,
          <span className="text-body-secondary">
            <FormattedMessage
              id="account.status.choice.pillar.second.withdrawalContributionEndingDisclaimer.singular"
              values={{
                b: (chunks: string) => <b>{chunks}</b>,
                contributionEndDate: formatDate(mandateDeadlines?.secondPillarContributionEndDate),
              }}
            />
          </span>,
        ]}
      />
    );
  }

  return null;
};

const ActiveFundPension = ({ fundPension }: { fundPension: FundPension }) => (
  <StatusBoxRow
    status="SUCCESS"
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    showAction={false}
    lines={[
      <FormattedMessage id="account.status.choice.pillar.second.fundPension.active" />,
      <ActiveFundPensionDescription fundPension={fundPension} />,
    ]}
  />
);

const HighFee = ({ loading, conversion, sourceFunds, targetFunds, leaveApplication }: RowProps) => {
  const value = getValueSum(sourceFunds);
  const currentFeesEuro = conversion.weightedAverageFee * value;
  const tulevaTargetFunds =
    targetFunds && targetFunds.length && targetFunds.filter((fund) => isTuleva(fund));
  const defaultTargetFund =
    tulevaTargetFunds && tulevaTargetFunds.length ? tulevaTargetFunds[0] : null;
  const tulevaFeesPercent = defaultTargetFund?.ongoingChargesFigure || 0;
  const tulevaFeesEuro = tulevaFeesPercent * value;

  return (
    <StatusBoxRow
      status="WARNING"
      showAction={!loading}
      name={<FormattedMessage id="account.status.choice.pillar.second" />}
      lines={[
        <>
          <FormattedMessage id="account.status.choice.highFee.label" />
          <InfoTooltip name="second-pillar-tooltip">
            <FormattedMessage id="account.status.choice.highFee.description" />
          </InfoTooltip>
        </>,
        ...(currentFeesEuro >= 0.5 && tulevaFeesEuro >= 0.5 && currentFeesEuro - tulevaFeesEuro >= 1
          ? [<FeeComparison currentFeesEuro={currentFeesEuro} tulevaFeesEuro={tulevaFeesEuro} />]
          : []),
      ]}
    >
      <SecondPillarActionButton leaveApplication={leaveApplication} className="btn-primary" />
    </StatusBoxRow>
  );
};

const usePendingEarlyWithdrawalApplication = (): Application | undefined =>
  usePendingApplications().data?.find(
    (application) => application.type === 'WITHDRAWAL' || application.type === 'EARLY_WITHDRAWAL',
  );

const usePendingPartialWithdrawalApplication = (): Application | undefined =>
  usePendingApplications().data?.find((application) => application.type === 'PARTIAL_WITHDRAWAL');

const usePendingFundPensionOpeningApplication = (): Application | undefined =>
  usePendingApplications().data?.find((application) => application.type === 'FUND_PENSION_OPENING');

const mapStateToProps = (state: State) => ({
  loading: state.login.loadingUserConversion,
  conversion: state.login.userConversion.secondPillar,
  sourceFunds: state.exchange.sourceFunds || [],
  targetFunds: state.exchange.targetFunds || [],
  secondPillarActive: state.login.user?.secondPillarActive,
  currentPaymentRate: state.login.user?.secondPillarPaymentRates?.current ?? 2,
  pendingPaymentRate:
    state.login.user?.secondPillarPaymentRates?.pending ??
    state.login.user?.secondPillarPaymentRates?.current ??
    2,
  activeFundIsin: state.exchange.sourceFunds?.find((sourceFund) => sourceFund.activeFund)?.isin,
});

const SecondPillarMissing = ({ loading, nudgeKey }: RowProps) => (
  <StatusBoxRow
    status="ERROR"
    showAction={!loading}
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    lines={[<FormattedMessage id="account.status.choice.pillar.second.missing.label" />]}
  >
    {nudgeKey === 'SECOND_PILLAR_START' && (
      <Link to="/2nd-pillar-flow" className="btn btn-primary">
        <FormattedMessage id="account.status.choice.pillar.second.missing.action" />
      </Link>
    )}
  </StatusBoxRow>
);

const IncreasePaymentRate = ({
  loading,
  currentPaymentRate,
  pendingPaymentRate,
  mandateDeadlines,
}: RowProps) => (
  <StatusBoxRow
    status="WARNING"
    showAction={!loading}
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    lines={[
      <PaymentRateSubRow
        currentPaymentRate={currentPaymentRate}
        futurePaymentRate={pendingPaymentRate}
        paymentRateFulfillmentDate={mandateDeadlines?.paymentRateFulfillmentDate ?? ''}
      />,
      <span className="text-body-secondary">
        <SecondPillarPaymentRateTaxWin />
      </span>,
    ]}
  >
    <Link to="/2nd-pillar-payment-rate" className="btn btn-primary">
      <FormattedMessage id="account.status.choice.paymentRate.increase" />
    </Link>
  </StatusBoxRow>
);

const FullyConvertedToTulevaBonds = ({
  loading,
  currentPaymentRate,
  pendingPaymentRate,
  mandateDeadlines,
}: RowProps) => (
  <StatusBoxRow
    status="WARNING"
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    showAction={!loading}
    lines={[
      <>
        <FormattedMessage id="account.status.choice.tulevaBondFund.index.label" />
        <InfoTooltip name="bonds-fund-tooltip">
          <>
            <p className="m-0 fw-bold">
              <FormattedMessage id="target.funds.EE3600109443.title" />
            </p>
            <p className="m-0 mt-2">
              <FormattedMessage id="target.funds.EE3600109443.description" />
            </p>
            <p className="m-0 mt-2">
              <FormattedMessage id="target.funds.EE3600109443.description.2" />
            </p>
          </>
        </InfoTooltip>
      </>,
      <span className="text-body-secondary">
        <PaymentRateSubRow
          currentPaymentRate={currentPaymentRate}
          futurePaymentRate={pendingPaymentRate}
          paymentRateFulfillmentDate={mandateDeadlines?.paymentRateFulfillmentDate ?? ''}
        />
      </span>,
    ]}
  >
    <Link to="/2nd-pillar-flow" className="btn btn-outline-primary">
      <FormattedMessage id="account.status.choice.choose.stock.fund" />
    </Link>
  </StatusBoxRow>
);

const FullyConvertedToTuleva = ({
  loading,
  currentPaymentRate,
  pendingPaymentRate,
  mandateDeadlines,
}: RowProps) => (
  <StatusBoxRow
    status="SUCCESS"
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    showAction={!loading}
    lines={[
      <FormattedMessage id="account.status.choice.lowFee.index.label" />,
      <span className="text-body-secondary">
        <PaymentRateSubRow
          currentPaymentRate={currentPaymentRate}
          futurePaymentRate={pendingPaymentRate}
          paymentRateFulfillmentDate={mandateDeadlines?.paymentRateFulfillmentDate ?? ''}
        />
      </span>,
      <span className="text-body-secondary">
        <SecondPillarPaymentRateTaxWin />
      </span>,
    ]}
  />
);

const InLowFeeFund = ({
  loading,
  currentPaymentRate,
  pendingPaymentRate,
  mandateDeadlines,
  leaveApplication,
}: RowProps) => (
  <StatusBoxRow
    status="SUCCESS"
    showAction={!loading}
    name={<FormattedMessage id="account.status.choice.pillar.second" />}
    lines={[
      <FormattedMessage id="account.status.choice.lowFee.label" />,
      <span className="text-body-secondary">
        <PaymentRateSubRow
          currentPaymentRate={currentPaymentRate}
          futurePaymentRate={pendingPaymentRate}
          paymentRateFulfillmentDate={mandateDeadlines?.paymentRateFulfillmentDate ?? ''}
        />
      </span>,
      <span className="text-body-secondary">
        <SecondPillarPaymentRateTaxWin />
      </span>,
    ]}
  >
    <SecondPillarActionButton leaveApplication={leaveApplication} className="btn-outline-primary" />
  </StatusBoxRow>
);

export default connect(mapStateToProps)(SecondPillarStatusBox);
